import { useEffect, useState } from "react";
import { createPortal } from "react-dom";
import { usePriceCorrection } from "@/hooks/usePriceCorrection";
import { toast } from "@/lib/toast";

interface Props {
  open: boolean;
  onClose: () => void;
  scanId: string;
  currentPrice: number;
  decisionId?: string;
}

export function PriceCorrectionDialog({
  open,
  onClose,
  scanId,
  currentPrice,
  decisionId,
}: Props) {
  const [priceStr, setPriceStr] = useState("");
  const [notes, setNotes] = useState("");
  const correction = usePriceCorrection();
  const { reset: resetCorrection } = correction;

  useEffect(() => {
    if (open) {
      setPriceStr("");
      setNotes("");
      resetCorrection();
    }
    // resetCorrection is stable across renders (mutation.reset from TanStack
    // Query is referentially stable); intentionally not in deps to avoid the
    // re-render loop caused by the parent hook returning a new object each
    // render.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  if (!open) return null;

  const parsed = priceStr.trim() === "" ? null : Number(priceStr);
  const valid = parsed !== null && Number.isFinite(parsed) && parsed >= 0 && parsed < 100000;

  const submit = async () => {
    if (!valid || parsed === null) return;
    try {
      await correction.submitAsync({
        scan_id: scanId,
        corrected_price: Math.round(parsed),
        notes: notes.trim() || undefined,
        decision_id: decisionId,
      });
      toast.success("Thanks! That helps.");
      onClose();
    } catch (err) {
      const msg = err instanceof Error ? err.message : String(err);
      toast.error("Could not save correction", { description: msg });
    }
  };

  const ink = "hsl(var(--ink))";
  const paper = "hsl(var(--paper))";

  // Portal to document.body + constrain the inner panel to max-width 430
  // (the MobileFrame width). Without this, on desktop web the sheet
  // spreads across the whole browser window instead of staying within
  // the phone-frame footprint. Pattern mirrors NewMessageModal.
  const sheet = (
    <div
      role="dialog"
      aria-modal="true"
      aria-labelledby="price-correction-title"
      onClick={onClose}
      style={{
        position: "fixed",
        inset: 0,
        zIndex: 100,
        background: "rgba(20, 14, 7, 0.6)",
        display: "flex",
        alignItems: "flex-end",
        justifyContent: "center",
        touchAction: "none",
      }}
    >
      <div
        onClick={(e) => e.stopPropagation()}
        style={{
          width: "100%",
          maxWidth: 430,
          background: paper,
          color: ink,
          padding: "20px 16px calc(env(safe-area-inset-bottom, 16px) + 16px)",
          borderTop: `1px solid ${ink}`,
          maxHeight: "85vh",
          overflowY: "auto",
          overscrollBehavior: "contain",
          WebkitOverflowScrolling: "touch",
        }}
      >
        <h2
          id="price-correction-title"
          style={{
            fontFamily: "'Fraunces', serif",
            fontWeight: 500,
            fontSize: 22,
            margin: 0,
            marginBottom: 6,
          }}
        >
          Correct this price
        </h2>
        <p style={{ fontSize: 13, opacity: 0.7, marginBottom: 16, lineHeight: 1.5 }}>
          FIG suggested <strong>${currentPrice}</strong>. If you think the right price is
          different — based on a sold comp, your knowledge, or a recent listing — tell us.
          We use these to improve future pricing.
        </p>

        <label
          htmlFor="price-correction-input"
          style={{
            display: "block",
            fontFamily: "'IBM Plex Mono', monospace",
            fontSize: 11,
            letterSpacing: "0.18em",
            textTransform: "uppercase",
            marginBottom: 6,
            opacity: 0.7,
          }}
        >
          Your estimate (USD)
        </label>
        <input
          id="price-correction-input"
          inputMode="numeric"
          value={priceStr}
          onChange={(e) => setPriceStr(e.target.value)}
          disabled={correction.isPending}
          placeholder="e.g. 95"
          style={{
            width: "100%",
            padding: "10px 12px",
            border: `1px solid ${ink}`,
            background: paper,
            color: ink,
            marginBottom: 12,
            fontFamily: "'IBM Plex Mono', monospace",
            fontSize: 13,
          }}
        />

        <label
          htmlFor="price-correction-notes"
          style={{
            display: "block",
            fontFamily: "'IBM Plex Mono', monospace",
            fontSize: 11,
            letterSpacing: "0.18em",
            textTransform: "uppercase",
            marginBottom: 6,
            opacity: 0.7,
          }}
        >
          Notes (optional)
        </label>
        <textarea
          id="price-correction-notes"
          value={notes}
          onChange={(e) => setNotes(e.target.value)}
          disabled={correction.isPending}
          placeholder="Sold comp link, observation, etc."
          rows={3}
          style={{
            width: "100%",
            padding: "10px 12px",
            border: `1px solid ${ink}`,
            background: paper,
            color: ink,
            marginBottom: 16,
            fontFamily: "'IBM Plex Mono', monospace",
            fontSize: 13,
            resize: "vertical",
          }}
        />

        <div style={{ display: "flex", gap: 8 }}>
          <button
            type="button"
            onClick={onClose}
            disabled={correction.isPending}
            style={{
              flex: 1,
              padding: "12px",
              border: `1px solid ${ink}`,
              background: paper,
              color: ink,
              fontFamily: "'IBM Plex Mono', monospace",
              fontSize: 12,
              letterSpacing: "0.18em",
              textTransform: "uppercase",
              cursor: correction.isPending ? "wait" : "pointer",
            }}
          >
            Cancel
          </button>
          <button
            type="button"
            onClick={submit}
            disabled={!valid || correction.isPending}
            style={{
              flex: 1,
              padding: "12px",
              border: `1px solid ${ink}`,
              background: ink,
              color: paper,
              fontFamily: "'IBM Plex Mono', monospace",
              fontSize: 12,
              letterSpacing: "0.18em",
              textTransform: "uppercase",
              cursor: !valid || correction.isPending ? "wait" : "pointer",
              opacity: !valid || correction.isPending ? 0.5 : 1,
            }}
          >
            {correction.isPending ? "Saving…" : "Submit"}
          </button>
        </div>
      </div>
    </div>
  );

  return createPortal(sheet, document.body);
}
